// This is the base for splide carousel, the full documentation can be found on https://splidejs.com/events/
// The Grid extension is just used when the carousel display are not in line but on a group,
// So the carousel changes the whole group of elements and not one by one.
// The grid extensions and functions can be deleted if it's not in the website design.

import Splide from '@splidejs/splide';
import { merge } from 'lodash';
import { Grid } from '@splidejs/splide-extension-grid';

const bootstrapBreakpoints = {
	xs: 0,
	sm: 576,
	md: 768,
	lg: 992,
	xl: 1200,
	xxl: 1400,
};

const splideArrowPath =
	'M0.513074 5.95241L4.2414 9.68074C4.41719 9.85052 4.65264 9.94447 4.89703 9.94235C5.14142 9.94022 5.3752 9.8422 5.54801 9.66938C5.72083 9.49657 5.81885 9.26279 5.82098 9.0184C5.8231 8.77401 5.72915 8.53857 5.55937 8.36277L2.49002 5.29343L5.55937 2.22408C5.72915 2.04829 5.8231 1.81284 5.82098 1.56845C5.81885 1.32406 5.72083 1.09029 5.54801 0.91747C5.3752 0.744655 5.14142 0.646628 4.89703 0.644504C4.65264 0.642381 4.4172 0.73633 4.2414 0.906115L0.513074 4.63444C0.338336 4.80924 0.240173 5.04627 0.240173 5.29343C0.240173 5.54058 0.338336 5.77762 0.513074 5.95241Z';

const splideConfiguration = {
	default: {
		arrowPath: splideArrowPath,
		type: 'loop',
		start: 0,
		perPage: 3,
		pagination: true,
		focus: 1,
		mediaQuery: 'min',
		breakpoints: {
			640: {
				perPage: 3,
			},
		},
	},

	'announcement-carousel': {
		arrowPath: splideArrowPath,
		type: 'loop',
		start: 0,
		perPage: 1,
		perMove: 1,
		focus: 0,
		pagination: true,
		arrows: true,
		autoplay: true,
		breakpoints: {
			640: {
				perPage: 1,
				focus: 0,
			},
		},
	},

	'corporate-carousel': {
		type: 'loop',
		autoplay: true,
		start: 0,
		perMove: 1,
		focus: 0,
		grid: {
			rows: 2,
			cols: 1,
		},
		pagination: true,
		arrows: false,
		mediaQuery: 'min',
		breakpoints: {
			320: {
				perPage: 2,
				destroy: false,
			},
			540: {
				perPage: 3,
				destroy: false,
			},
			900: {
				perPage: 4,
				destroy: false,
			},
			993: {
				destroy: true,
			},
		},
	},

	'veterinary-carousel': {
		arrowPath: splideArrowPath,
		type: 'loop',
		start: 0,
		perPage: 1,
		perMove: 1,
		focus: 0,
		grid: {
			rows: 1,
			cols: 4,
		},
		pagination: true,
		arrows: true,
		autoplay: true,
		breakpoints: {
			540: {
				perPage: 1,
				focus: 0,
				grid: {
					rows: 1,
					cols: 1,
				},
			},
			640: {
				perPage: 1,
				focus: 0,
				grid: {
					rows: 1,
					cols: 2,
				},
			},
			900: {
				perPage: 1,
				focus: 0,
				grid: {
					rows: 1,
					cols: 3,
				},
			}
		},
	},

	'stories-carousel': {
		arrowPath: splideArrowPath,
		type: 'loop',
		start: 0,
		perPage: 1,
		perMove: 1,
		focus: 0,
		pagination: true,
		arrows: true,
		autoplay: true,
		breakpoints: {
			640: {
				perPage: 1,
				focus: 0,
			},
		},
	},

	'tabs-carousel': {
		arrowPath: splideArrowPath,
		type: 'loop',
		start: 0,
		perPage: 1,
		perMove: 1,
		focus: 0,
		pagination: false,
		arrows: true,
		mediaQuery: 'min',
		breakpoints: {
			320: {
				destroy: false,
			},
			768: {
				destroy: false,
			},
			992: {
				destroy: false,
			},
			993: {
				destroy: true,
			},
		},
	},

	'text-carousel': {
		arrowPath: splideArrowPath,
		type: 'loop',
		start: 0,
		perPage: 1,
		perMove: 1,
		focus: 0,
		pagination: true,
		arrows: true,
		breakpoints: {
			640: {
				perPage: 1,
				focus: 0,
			},
		},
	},
};

function initializeSplide() {
	const splides = document.getElementsByClassName( 'splide' );
	[ ...splides ].forEach( ( splide ) => {
		let config = splideConfiguration.default;

		const configAttr = splide.getAttribute( 'data-splide-config' );
		if ( configAttr !== undefined && configAttr !== '' ) {
			config = splideConfiguration[ configAttr ];
		}

		// Check for disable settings
		const disableAttr = splide.getAttribute( 'data-splide-disable' );
		if ( disableAttr !== null && disableAttr !== '' ) {
			const disableBreakpoints = disableAttr.split( ',' );

			// Track if a breakpoint has been disabled.
			let disableFlag = false;

			for ( const breakpoint in bootstrapBreakpoints ) {
				if ( disableBreakpoints.includes( breakpoint ) ) {
					const disableObj = {};
					disableObj[ bootstrapBreakpoints[ breakpoint ] ] = {
						destroy: true,
					};

					config = merge( config, {
						breakpoints: disableObj,
					} );

					disableFlag = true;
				} else if ( disableFlag === true ) {
					const disableObj = {};
					disableObj[ bootstrapBreakpoints[ breakpoint ] ] = {
						destroy: false,
					};

					config = merge( config, {
						breakpoints: disableObj,
					} );

					disableFlag = false;
				}
			}
		}

		if (configAttr === null) return;

		switch(configAttr) {
			case "corporate-carousel": {
				new Splide( splide, config ).mount( { Grid } );
				break;
			}

			case "veterinary-carousel": {
				new Splide( splide, config ).mount( { Grid } );
				break;
			}

			case "tabs-carousel":{
				const tabsSplideCarousel = new Splide( splide, config ).mount();

				let activeTabs = splide.querySelector(`.tab-pane#nav-${tabsSplideCarousel.index}`);

				tabsSplideCarousel.on('mounted move', (index) => {
					activeTabs.classList.remove('show', 'active');

					activeTabs = splide.querySelector(`.tab-pane#nav-${index ?? tabsSplideCarousel.index}`);
					activeTabs.classList.add('show','active');
					gsap.from(activeTabs, {opacity: 0, duration: 0.5});
					activeTabs.style.opacity = 1;
				});

				break;
			}

			default: {
				new Splide( splide, config ).mount();
				break;
			}
		}

	} );
}

document.addEventListener( 'DOMContentLoaded', () => {
	initializeSplide();
} );
